// Match Live View noise floor — distinguishes data-lake generation from legacy aux1 consumption.
const MIN_AUX1_SOLAR_GENERATION_MW = 50000
const W_TO_MW = 1000

/** DC units whose data-lake GraphQL payloads use different sign / scalar topology. */
const DATA_LAKE_DC_HARDWARE_IDS = new Set([
  '005b897e',
  '00688b76',
  '0068876c',
  'dd6b39b7',
  'dd670006'
])

function isDataLakeDcHardwareId(hardwareId) {
  if (hardwareId == null) return false
  return DATA_LAKE_DC_HARDWARE_IDS.has(String(hardwareId).toLowerCase())
}

/**
 * Convert aux1 clamp power (mW) to signed solarAll (positive = generating, negative = consumption).
 * Influx-sign: negative when generating. Data-lake / GraphQL: positive when generating.
 */
function ffrAux1ToSolarAll(ffrAux1) {
  if (ffrAux1 == null) return null
  if (ffrAux1 < 0) return -ffrAux1
  if (ffrAux1 >= MIN_AUX1_SOLAR_GENERATION_MW) return ffrAux1
  return -ffrAux1
}

function wattsToMw(w) {
  if (w == null) return null
  const n = Number(w)
  return Number.isFinite(n) ? n * W_TO_MW : null
}

function createdAtToIso(createdAt) {
  if (createdAt == null) return null
  const n = Number(createdAt)
  if (!Number.isFinite(n)) return null
  const ms = n > 1e11 ? n : n * 1000
  return new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z')
}

function emptyTotals() {
  return {
    solarConsumed: 0,
    solarGenerated: 0,
    gridImports: 0,
    gridExports: 0,
    homeConsumed: 0,
    batteryCharge: 0,
    batteryDischarge: 0,
    batteryOutputExported: 0,
    aux2Consumed: 0,
    aux3Consumed: 0
  }
}

const REACT_APP_CLONED_DATA_MODE = process.env.REACT_APP_CLONED_DATA_MODE === 'true'
const CLUSTER_MASTER_ONLY_KEYS = new Set(['gridPower', 'aux1Power'])
const CLUSTER_SUM_KEYS = ['batteryPower', 'aux2Power', 'aux3Power']
const CLUSTER_AVG_KEYS = ['socUsable', 'socAverage']

function sumFinite(values) {
  const nums = values.filter((v) => typeof v === 'number' && Number.isFinite(v))
  if (!nums.length) return null
  return nums.reduce((a, b) => a + b, 0)
}

function averageFinite(values) {
  const nums = values.filter((v) => typeof v === 'number' && Number.isFinite(v))
  if (!nums.length) return null
  return nums.reduce((a, b) => a + b, 0) / nums.length
}

function clusterMasterId(value) {
  if (value == null) return null
  const id = String(value).trim().toLowerCase()
  return id || null
}

/**
 * Prefer unit.clusterMaster (same ID on every member), then match hardwareId.
 * Defaults to 0 so a cluster never falls back to summing shared clamps.
 */
function resolveClusterMasterIndex(units) {
  let masterId = null
  for (const unit of units || []) {
    const id = clusterMasterId(unit?.clusterMaster)
    if (id) {
      masterId = id
      break
    }
  }
  if (masterId == null) return 0
  for (let j = 0; j < units.length; j++) {
    if (clusterMasterId(units[j]?.hardwareId) === masterId) return j
  }
  return 0
}

/**
 * Merge GraphQL unitStates the way chart clustering merges telemetry:
 * cluster → grid/aux1 (solar) from master only, batteries summed, SoC averaged.
 * Independent units → sum all powers.
 */
function mergeClusterUnitStates(unitStates, { isCluster, masterIndex, unitsCount }) {
  const master = (masterIndex != null && unitStates[masterIndex]) || unitStates.find((s) => s) || {}
  const merged = {}

  for (const key of CLUSTER_MASTER_ONLY_KEYS) {
    if (isCluster && masterIndex != null && unitStates[masterIndex]) {
      merged[key] = unitStates[masterIndex][key] ?? null
    } else {
      merged[key] = sumFinite(unitStates.map((s) => (s ? s[key] : null)))
    }
  }

  for (const key of CLUSTER_SUM_KEYS) {
    const sum = sumFinite(unitStates.map((s) => (s ? s[key] : null)))
    merged[key] =
      key === 'batteryPower' && REACT_APP_CLONED_DATA_MODE === true && unitsCount > 0
        ? sum / unitsCount
        : sum
  }

  for (const key of CLUSTER_AVG_KEYS) {
    merged[key] = averageFinite(unitStates.map((s) => (s ? s[key] : null)))
  }

  if (merged.socUsable == null && master.socUsable != null) merged.socUsable = master.socUsable
  if (merged.socAverage == null && master.socAverage != null) merged.socAverage = master.socAverage

  return merged
}

function sumLiveViewFields(rows) {
  const acc = { ...rows[0] }
  for (let i = 1; i < rows.length; i++) {
    const row = rows[i]
    acc.instant_battery = (acc.instant_battery || 0) + (row.instant_battery || 0)
    acc.instant_demand = (acc.instant_demand || 0) + (row.instant_demand || 0)
    acc.instant_grid = (acc.instant_grid || 0) + (row.instant_grid || 0)
    acc.instant_solar = (acc.instant_solar || 0) + (row.instant_solar || 0)
    acc.solarGenerated = (acc.solarGenerated || 0) + (row.solarGenerated || 0)
    acc.instant_aux2 = (acc.instant_aux2 || 0) + (row.instant_aux2 || 0)
    acc.instant_aux3 = (acc.instant_aux3 || 0) + (row.instant_aux3 || 0)
    acc.instant_soc = ((acc.instant_soc || 0) * i + (row.instant_soc || 0)) / (i + 1)
  }
  return acc
}

/**
 * Live View cluster merge. Shared solar/grid clamps are master-only (pre-1d5ccbd
 * sumChartData / createClustersAndMergeRawData). Batteries still sum.
 */
function mergeLiveViewUnitStates(unitsData) {
  const usable = (unitsData || []).filter((ud) => ud?.unitState && typeof ud.unitState === 'object')
  if (!usable.length) return false

  const isCluster = typeof usable[0].unit?.clusterHash === 'string'

  const groups = {}
  for (const ud of usable) {
    let hash = ud.unit?.clusterHash
    if (!isCluster || !hash) hash = 'no-cluster'
    if (!groups[hash]) groups[hash] = []
    groups[hash].push(ud)
  }

  const groupFields = []
  for (const group of Object.values(groups)) {
    const units = group.map((g) => g.unit)
    const masterIndex = isCluster ? resolveClusterMasterIndex(units) : null
    const mergedState = mergeClusterUnitStates(
      group.map((g) => g.unitState),
      { isCluster, masterIndex, unitsCount: group.length }
    )
    const hardwareId = group[masterIndex ?? 0]?.unit?.hardwareId
    const fields = unitStateToLiveViewFields(mergedState, hardwareId)
    if (fields) groupFields.push(fields)
  }

  if (!groupFields.length) return false
  if (groupFields.length === 1) return groupFields[0]
  return sumLiveViewFields(groupFields)
}

/**
 * GraphQL `bessUnitState` → Live View instant_* fields (mW), matching chart calculatedFields signs.
 */
function unitStateToLiveViewFields(unitState, hardwareId) {
  if (!unitState || typeof unitState !== 'object') return false

  const gridMw = wattsToMw(unitState.gridPower)
  const batteryMw = wattsToMw(unitState.batteryPower)
  const aux1Mw = wattsToMw(unitState.aux1Power) ?? 0
  const aux2Mw = wattsToMw(unitState.aux2Power)
  const aux3Mw = wattsToMw(unitState.aux3Power)
  const soc = unitState.socUsable ?? unitState.socAverage ?? 0

  if (gridMw == null || batteryMw == null) return false

  const useDataLakeDcCalc = isDataLakeDcHardwareId(hardwareId)
  const solarAll = ffrAux1ToSolarAll(aux1Mw) ?? 0
  const isDataLakeScalarDc =
    useDataLakeDcCalc && solarAll >= MIN_AUX1_SOLAR_GENERATION_MW
  const batteryAll = isDataLakeScalarDc ? batteryMw + solarAll : batteryMw

  let homeConsumed
  if (isDataLakeScalarDc) {
    homeConsumed = Math.max(0, Math.max(0, solarAll) + Math.max(0, -batteryAll) - Math.max(0, -gridMw))
  } else {
    homeConsumed = Math.max(0, gridMw - batteryMw - aux1Mw)
  }

  return {
    instant_battery: -batteryAll,
    instant_demand: -homeConsumed,
    instant_grid: gridMw,
    instant_solar: solarAll,
    solarGenerated: Math.max(0, solarAll),
    instant_soc: soc,
    instant_aux2: aux2Mw,
    instant_aux3: aux3Mw,
    gridClampDisconnected: 0,
    dnoOverride: 0,
    epsGridLoss: 0,
    epsOverConsumption: 0,
    epsLowSoC: 0
  }
}

function calculatedFields(unitsData) {
  /*
      unitsData = {
        units: [ ... ],
        telemetry: [
          { createdAt, gridPower, batteryPower, aux1Power, aux2Power, aux3Power, socUsable, socAverage },
          ...
        ]
      }
    */

  const { unit, units, telemetry } = unitsData
  const hardwareId = unit?.hardwareId ?? units?.[0]?.hardwareId
  const useDataLakeDcCalc = isDataLakeDcHardwareId(hardwareId)

  if (!Array.isArray(telemetry) || !telemetry.length) {
    // Totals-only loads have no series; keep API `bessEnergyTotals` as-is.
    return {
      units: units ? units : [unit],
      series: null,
      totals: unitsData.totals
    }
  }

  const returnRows = []
  const totals = emptyTotals()

  for (const sample of telemetry) {
    const calculated = {}
    const time = createdAtToIso(sample.createdAt)

    const FFR_LOCAL = wattsToMw(sample.batteryPower)
    const FFR_HOUSE = wattsToMw(sample.gridPower)
    const FFR_AUX1 = wattsToMw(sample.aux1Power) ?? 0
    const FFR_AUX2 = wattsToMw(sample.aux2Power)
    const FFR_AUX3 = wattsToMw(sample.aux3Power)
    const socUsable = sample.socUsable ?? null

    let batteryAll = null
    let solarAll = null
    let isDataLakeScalarDc = false
    calculated.batteryInputFromGrid = null
    calculated.batteryInputFromSolar = null
    calculated.batteryOutputConsumedByHome = null
    calculated.batteryOutputExported = null
    calculated.homeConsumed = null
    calculated.gridConsumedByHome = null
    calculated.solarConsumedByHome = null
    calculated.solarExported = null
    calculated.instant_battery = null
    calculated.instant_demand = null
    calculated.instant_grid = null
    calculated.solarGenerated = null
    calculated.solarConsumption = null
    calculated.instant_solar = null
    calculated.instant_soc = null
    calculated.dnoOverride = null
    calculated.accessoryClamp1 = null
    calculated.smartHomeDevices = {}
    calculated.hardwareIdChanged = Boolean(sample.hardwareIdChanged)

    if (FFR_LOCAL != null && FFR_HOUSE != null) {
      const solarFromAux1 = ffrAux1ToSolarAll(FFR_AUX1) ?? 0
      // GraphQL `batteryPower` is the former Influx FFR_LOCAL. For these DC units the
      // data-lake payload is combined inverter local power (the old reshape also copied
      // it to INVT_BATTERY), so generation-scale aux1 uses the scalar split.
      isDataLakeScalarDc =
        useDataLakeDcCalc && solarFromAux1 >= MIN_AUX1_SOLAR_GENERATION_MW

      if (isDataLakeScalarDc) {
        solarAll = solarFromAux1
        batteryAll = FFR_LOCAL + solarAll
      } else {
        batteryAll = FFR_LOCAL
        solarAll = useDataLakeDcCalc ? solarFromAux1 : -FFR_AUX1
      }

      if (batteryAll != null && solarAll != null) {
        const isSimpleAux1Path = useDataLakeDcCalc && !isDataLakeScalarDc
        if (isDataLakeScalarDc) {
          calculated.homeConsumed = Math.max(
            0,
            Math.max(0, solarAll) + Math.max(0, -batteryAll) - Math.max(0, -FFR_HOUSE)
          )
        } else {
          calculated.homeConsumed = Math.max(0, FFR_HOUSE - FFR_LOCAL - FFR_AUX1)
        }
        calculated.solarGenerated = Math.max(0, solarAll)
        calculated.solarConsumption = Math.max(0, -solarAll)
        const batteryOutput = Math.max(0, -batteryAll)
        calculated.batteryOutputExported = Math.min(batteryOutput, Math.max(0, -FFR_HOUSE))
        calculated.batteryOutputConsumedByHome = batteryOutput - calculated.batteryOutputExported
        const batteryInput = Math.max(0, batteryAll)
        calculated.batteryInputFromGrid = Math.min(batteryInput, Math.max(0, FFR_HOUSE))
        calculated.batteryInputFromSolar = batteryInput - calculated.batteryInputFromGrid
        calculated.gridConsumedByHome = Math.max(0, FFR_HOUSE) - calculated.batteryInputFromGrid
        calculated.solarConsumedByHome = Math.min(calculated.solarGenerated, calculated.homeConsumed)
        if (
          isSimpleAux1Path &&
          FFR_AUX1 != null &&
          FFR_AUX1 >= MIN_AUX1_SOLAR_GENERATION_MW &&
          calculated.homeConsumed === 0 &&
          calculated.solarGenerated > 0 &&
          calculated.batteryOutputConsumedByHome > 0
        ) {
          const solarToHome = Math.min(
            calculated.solarGenerated,
            calculated.batteryOutputConsumedByHome
          )
          calculated.solarConsumedByHome = solarToHome
          calculated.batteryOutputConsumedByHome -= solarToHome
        }
        const gridExportsMw = Math.max(0, -FFR_HOUSE)
        const solarExportedFromGrid = Math.max(0, gridExportsMw - calculated.batteryOutputExported)
        const solarExportedFromGeneration = Math.max(
          0,
          calculated.solarGenerated -
            calculated.solarConsumedByHome -
            calculated.batteryInputFromSolar
        )
        if (
          isSimpleAux1Path &&
          solarExportedFromGrid === 0 &&
          solarExportedFromGeneration > 0 &&
          gridExportsMw > 0
        ) {
          calculated.solarExported = Math.min(solarExportedFromGeneration, gridExportsMw)
        } else {
          calculated.solarExported = solarExportedFromGrid
        }

        calculated.instant_battery = -batteryAll
        calculated.instant_demand = -calculated.homeConsumed
        calculated.instant_solar = solarAll
        calculated.instant_grid = FFR_HOUSE
        calculated.instant_soc = socUsable
        calculated.hasInternetAccess = null
        calculated.instant_aux2 = FFR_AUX2
        calculated.instant_aux3 = FFR_AUX3
      }
    }

    calculated.gridClampDisconnected = 0
    calculated.dnoOverride = 0
    calculated.epsGridLoss = 0
    calculated.epsOverConsumption = 0
    calculated.epsLowSoC = 0

    totals.solarConsumed += (((calculated.solarGenerated || 0) - (calculated.solarExported || 0)) * 5) / 60
    totals.solarGenerated += ((calculated.solarGenerated || 0) * 5) / 60
    totals.gridImports += (Math.max(0, FFR_HOUSE || 0) * 5) / 60
    totals.gridExports += (Math.max(0, -(FFR_HOUSE || 0)) * 5) / 60
    totals.homeConsumed += ((calculated.homeConsumed || 0) * 5) / 60
    totals.batteryCharge += (Math.max(0, batteryAll || 0) * 5) / 60
    totals.batteryDischarge += (Math.max(0, -(batteryAll || 0)) * 5) / 60
    totals.aux2Consumed += ((calculated.instant_aux2 || 0) * 5) / 60
    totals.aux3Consumed += ((calculated.instant_aux3 || 0) * 5) / 60

    returnRows.push({
      time,
      ...calculated
    })
  }

  return {
    units: units ? units : [unit],
    series: returnRows,
    totals: unitsData.totals || totals
  }
}

export {
  calculatedFields,
  ffrAux1ToSolarAll,
  isDataLakeDcHardwareId,
  unitStateToLiveViewFields,
  mergeLiveViewUnitStates,
  resolveClusterMasterIndex,
  createdAtToIso,
  MIN_AUX1_SOLAR_GENERATION_MW
}
